import { isRttsStudioEnabled, isRttsUseMock } from '../config/featureFlags';
import type { StudioCheckoutPlan } from './api/types';

export type { StudioCheckoutPlan };

/**
 * Studio-wide swappable constants.
 * Flag reads are functions so tests/env flips are not frozen at import time.
 * Pricing + conversion copy live here — paywall/checkout read from this module.
 */

/** `STUDIO_ENABLED` — REACT_APP_RTTS_STUDIO_ENABLED exact "true". Default off. */
export function isStudioEnabled(): boolean {
  return isRttsStudioEnabled();
}

/** Mock seam — REACT_APP_RTTS_USE_MOCK; exact "false" → live, else mock. */
export function isStudioUseMock(): boolean {
  return isRttsUseMock();
}

/**
 * Billing/checkout gate. Exact REACT_APP_RTTS_BILLING_ENABLED "true".
 * Missing/unset env stays off (fail-safe). Production builds bake true via
 * frontend/cloudbuild.yaml + deploy-production defaults. Mock mode does not
 * auto-enable checkout CTAs. Tests that need the pay funnel must set the env.
 * Mirrors backend RTTS_BILLING_ENABLED.
 */
export function isStudioBillingEnabled(): boolean {
  return process.env.REACT_APP_RTTS_BILLING_ENABLED === 'true';
}

/**
 * Standalone music picker / post-delivery Add-ons. Exact
 * REACT_APP_RTTS_MUSIC_ENABLED "true". Production builds bake true via
 * frontend/cloudbuild.yaml + deploy-production defaults.
 */
export function isStudioMusicEnabled(): boolean {
  return process.env.REACT_APP_RTTS_MUSIC_ENABLED === 'true';
}

/**
 * One-time 5-reel pack CTA/checkout. Exact REACT_APP_RTTS_REEL_PACKS_ENABLED "true".
 * Missing/unset env stays off (fail-safe). Production builds bake true via
 * frontend/cloudbuild.yaml + deploy-production defaults, matching billing.
 * Requires billing enabled for checkout; this flag only reveals the pack CTA.
 */
export function isStudioReelPacksEnabled(): boolean {
  return process.env.REACT_APP_RTTS_REEL_PACKS_ENABLED === 'true';
}

/**
 * Recap Editor V2 shell (Phase 1 layout). Exact
 * REACT_APP_RTTS_RECAP_EDITOR_V2 "true". Missing/unset stays off.
 * Production deploy defaults this OFF until founder flag-on.
 */
export function isRecapEditorV2Enabled(): boolean {
  return process.env.REACT_APP_RTTS_RECAP_EDITOR_V2 === 'true';
}

/** Locked RTTS Pro pricing model (mirrors backend env; swappable). */
export const STUDIO_PRICING = {
  free: { reels: 1, label: '1 free reel — watermarked' },
  monthly: { priceLabel: '$19.99/mo', reels: 10 },
  annual: {
    priceLabel: '$199.99/yr',
    reels: 10,
    saveLabel: 'Save ~17%',
    /** Catalog landing state. Checkout uses whatever the toggle has selected. */
    defaultSelected: false as const,
  },
  paidCap: 10, // mirrors RTTS_REEL_CAP_PAID (default 10)
  pack5: { priceLabel: '$14.99', reels: 5, oneTime: true as const },
  /** Launch shows both; flip false to hide annual without a rewrite. */
  showAnnual: true,
} as const;

/** Frontend-owned checkout return routes — backend success/cancel URLs must match. */
export const STUDIO_CHECKOUT_ROUTES = {
  success: '/studio/upgrade/success',
  cancel: '/studio/upgrade/cancel',
} as const;

/** Stripe Customer Portal return (matches backend RTTS_PORTAL_RETURN_URL default). */
export const STUDIO_PORTAL_RETURN_ROUTE = '/studio/account';

/** Absolute production URLs for go-live RTTS_CHECKOUT_*_URL env (Step 5/6). */
export const STUDIO_CHECKOUT_PRODUCTION_URLS = {
  success: 'https://scoutlytics.gg/studio/upgrade/success',
  cancel: 'https://scoutlytics.gg/studio/upgrade/cancel',
} as const;

/** @deprecated Prefer STUDIO_PRICING — kept as a convenience alias for landing. */
export const PRICE_DISPLAY = STUDIO_PRICING.monthly.priceLabel;

/**
 * Signed-out landing reel. Must be a public static path — never a signed GCS
 * URL, never a member/job/candidate id in the filename. Served from the
 * frontend image at /assets/studio/*.
 */
export const STUDIO_LANDING_REEL_SRC: string | null =
  '/assets/studio/landing-reel.mp4';
export const STUDIO_LANDING_REEL_POSTER =
  '/assets/studio/landing-reel-poster.jpg';

/**
 * Public watch-page header. Horizontal lockup (SVG) on a stable public path
 * so nginx can inject it into proxied `/r/` HTML during an FE-first pin.
 */
export const STUDIO_PUBLIC_WATCH_LOGO_SRC =
  '/assets/studio/scoutlytics-studio-horizontal.svg';

/** Film-strip showcase art (public derivatives). Masters stay in brand/studio/. */
export const STUDIO_FILMSTRIP_SQUARE_SRC = '/assets/studio/filmstrip/square-360.webp';
export const STUDIO_FILMSTRIP_SQUARE_PNG = '/assets/studio/filmstrip/square-360.png';
export const STUDIO_FILMSTRIP_LANDSCAPE_SRC =
  '/assets/studio/filmstrip/landscape-1024.webp';
export const STUDIO_FILMSTRIP_LANDSCAPE_PNG =
  '/assets/studio/filmstrip/landscape-1024.png';
export const STUDIO_FILMSTRIP_LANDSCAPE_SRCSET =
  '/assets/studio/filmstrip/landscape-640.webp 640w, /assets/studio/filmstrip/landscape-1024.webp 1024w, /assets/studio/filmstrip/landscape-1600.webp 1600w';
export const STUDIO_FILMSTRIP_LANDSCAPE_SIZES =
  '(max-width: 430px) 100vw, min(960px, 100vw)';

/** Watch-page film-strip band tile (sprocket edge crop, horizontally tileable). */
export const STUDIO_FILMSTRIP_BAND_SRC = '/assets/studio/filmstrip/band.webp';
export const STUDIO_FILMSTRIP_BAND_PNG = '/assets/studio/filmstrip/band.png';

/** Job polling interval for the progress screen. */
export const POLL_INTERVAL_MS = 4000;

/** Watermark overlay poll — fast, capped. Overlay lives on the render worker. */
export const WATERMARK_POLL_INTERVAL_MS =
  process.env.NODE_ENV === 'test' ? 5 : 2000;
export const WATERMARK_POLL_MAX_MS =
  process.env.NODE_ENV === 'test' ? 80 : 6 * 60 * 1000;

/** Music/voice mix poll. Mux lives on the render worker (no 300s HTTP cap). */
export const MIX_POLL_INTERVAL_MS =
  process.env.NODE_ENV === 'test' ? 5 : 2000;
export const MIX_POLL_MAX_MS =
  process.env.NODE_ENV === 'test' ? 80 : 20 * 60 * 1000;

/**
 * Client backstop while polling an in-flight job.
 * Does NOT mean the job failed — the backend (reaper / terminal status) is
 * the authority. On hit, show "still processing", keep polling.
 * Sized for cap-length worst case: 75-min tape detect ~35m + render ~5m +
 * fetch + headroom → 60m. Tests use a short window via NODE_ENV=test.
 */
export const JOB_POLL_CEILING_MS =
  process.env.NODE_ENV === 'test' ? 80 : 60 * 60 * 1000;

/** @deprecated Use JOB_POLL_CEILING_MS — kept so older imports keep compiling. */
export const JOB_POLL_TIMEOUT_MS = JOB_POLL_CEILING_MS;

/** Library poll while any reel is in progress. */
export const REELS_LIBRARY_POLL_MS =
  process.env.NODE_ENV === 'test' ? 20 : 4500;

/** Dedupe window for focus + visibilitychange library refetch. */
export const REELS_LIBRARY_REVALIDATE_DEBOUNCE_MS =
  process.env.NODE_ENV === 'test' ? 20 : 1500;

/** Slow nav badge refresh when nothing is cutting (cheap app-wide). */
export const REELS_BADGE_IDLE_POLL_MS =
  process.env.NODE_ENV === 'test' ? 50 : 20_000;

/** Faster badge refresh while cuts are in flight. */
export const REELS_BADGE_ACTIVE_POLL_MS =
  process.env.NODE_ENV === 'test' ? 30 : 12_000;

/** Initial backoff after a badge poll failure. */
export const REELS_BADGE_BACKOFF_BASE_MS =
  process.env.NODE_ENV === 'test' ? 20 : 5_000;

/** Cap for exponential badge backoff. */
export const REELS_BADGE_BACKOFF_MAX_MS =
  process.env.NODE_ENV === 'test' ? 80 : 120_000;

/** Halt badge polling after this many consecutive failures. */
export const REELS_BADGE_MAX_FAILURES = 5;

/** Success-route entitlement re-poll (webhook is async). */
export const ENTITLEMENT_POLL_INTERVAL_MS =
  process.env.NODE_ENV === 'test' ? 10 : 2000;
export const ENTITLEMENT_POLL_MAX_ATTEMPTS = 6;

/**
 * Studio job status → operator-facing copy map.
 */
export const STUDIO_STATUS_COPY = {
  queued: 'In queue…',
  downloading: 'Pulling the tape…',
  source_ready: 'Tape secured',
  detecting: 'Finding the highlights…',
  detect_ready: 'Highlights locked',
  awaiting_curation: 'Pick your plays',
  rendering: 'Cutting your reel…',
  render_ready: 'Finishing…',
  delivered: 'player',
  failed: 'Something went wrong cutting this reel. Try again.',
} as const;

export type StudioJobStatus = keyof typeof STUDIO_STATUS_COPY;

/** Progress bar percentages (failed has no bar). */
export const STUDIO_STATUS_PROGRESS: Record<
  Exclude<StudioJobStatus, 'failed'>,
  number
> = {
  queued: 5,
  downloading: 10,
  source_ready: 20,
  detecting: 50, // mid of 30–70 while highlights are found
  detect_ready: 75,
  awaiting_curation: 78,
  rendering: 90,
  render_ready: 95,
  delivered: 100,
};

/** Terminal statuses — everything else (incl. unknown) is treated as in-progress. */
export function isStudioJobInProgress(status: string): boolean {
  return status !== 'delivered' && status !== 'failed';
}

export function studioStatusLabel(status: string): string {
  if (status in STUDIO_STATUS_COPY) {
    const key = status as StudioJobStatus;
    if (key === 'delivered') return 'Ready';
    if (key === 'failed') return 'Failed';
    return STUDIO_STATUS_COPY[key];
  }
  return 'Working on your reel…';
}

export const STUDIO_PRODUCT_NAME = 'Scoutlytics Studio';

/**
 * Minimum VOD duration (seconds) eligible for reel generation.
 * Product dial — tune from launch data (e.g. 300 for 5 min).
 */
export const MIN_REEL_VOD_SEC = 600;

/** Max length for the required user-supplied reel name. */
export const REEL_NAME_MAX_LENGTH = 80;

/** Document titles for `/studio/*` routes. */
export const STUDIO_DOCUMENT_TITLES = {
  base: STUDIO_PRODUCT_NAME,
  vods: `${STUDIO_PRODUCT_NAME} — Pick a VOD`,
  connect: `${STUDIO_PRODUCT_NAME} — Connect Twitch`,
  generate: `${STUDIO_PRODUCT_NAME} — Cutting your reel`,
  reel: `${STUDIO_PRODUCT_NAME} — Your reel`,
  upgrade: `${STUDIO_PRODUCT_NAME} — Plans`,
  upgradeSuccess: `${STUDIO_PRODUCT_NAME} — You're Pro`,
  upgradeCancel: `${STUDIO_PRODUCT_NAME} — Upgrade`,
  onboarding: `${STUDIO_PRODUCT_NAME} — Welcome`,
  account: `${STUDIO_PRODUCT_NAME} — Account`,
  reels: `${STUDIO_PRODUCT_NAME} — My Reels`,
} as const;

export const STUDIO_VALUE_PROP =
  'Turn your Twitch VOD into a game recap in ~15–30 minutes — hands off.';

/**
 * Length-scaled honest ETA. Calibrated to live v2 walls
 * (detect ≈ tape × 0.5 + render + fetch; 40-min tape delivered in ~34 min).
 * Under-promise: the high end of each band is at or above observed reality.
 */
export function formatEstimatedReelTime(durationSec?: number | null): string {
  if (!durationSec || durationSec <= 0) {
    return '~15–30 minutes';
  }
  const minutes = durationSec / 60;
  if (minutes <= 30) {
    return '~15–25 minutes';
  }
  if (minutes <= 45) {
    return '~20–40 minutes';
  }
  if (minutes <= 60) {
    return '~25–45 minutes';
  }
  if (minutes <= 75) {
    return '~35–55 minutes';
  }
  return '~40–60 minutes';
}

/** Conversion / paywall copy — single source; do not re-hardcode in pages. */
export const STUDIO_COPY = {
  paywallHeadline: "You've used your free reel — upgrade to Pro for 10 reels a month.",
  pricingHeadline: 'Plans',
  pricingValueLine:
    'Turn a full Twitch VOD into a game recap — hands-off.',
  pricingToggleMonthly: 'Monthly',
  pricingToggleAnnual: 'Annual',
  pricingFreeName: 'Free',
  pricingProName: 'Pro',
  pricingFreeCta: 'Start free',
  pricingProCta: 'Upgrade',
  pricingPackCta: 'Get 5 clean reels — $14.99 (one-time)',
  pricingPackName: 'Reel Pack',
  pricingPackValue: '5 clean reels. Pay once — no subscription.',
  pricingPackBestValueHint: 'Pro is best value for regulars — 10/mo + music/voice.',
  pricingComingSoon: 'Coming soon',
  pricingComingSoonHint: 'Checkout opens when billing is enabled.',
  pricingFreeValue:
    'The full story of your stream, auto-cut, in the order the game happened.',
  seePlansCta: 'See plans',
  landingCta: 'Connect Twitch',
  landingHeroPriceTemplate: 'From {price} · {free}',
  faqHeading: 'Questions',
  faqEtaQ: 'How long does a reel take?',
  faqEtaA:
    'Most reels are ready in about 20 minutes. Longer VODs can take up to ~30–60 minutes. You can leave the page — we keep cutting.',
  faqWatermarkQ: 'Do free reels have a watermark?',
  faqWatermarkA:
    'Yes. Free reels carry a watermark. Pro reels are clean.',
  faqCancelQ: 'Can I cancel?',
  faqCancelA: 'Yes. Cancel anytime. Pro access ends when the subscription ends.',
  connectHeadline: 'Connect Twitch',
  connectBody:
    'Sign in with Twitch so we can list your VODs and cut a reel from one you pick. We only work with your own channel.',
  connectCta: 'Connect Twitch',
  onboardingSubhead: 'Your Twitch VODs become game recaps — hands off.',
  onboardingStepConnect: 'Connect your Twitch channel',
  onboardingStepPick: 'Pick a VOD',
  onboardingStepWait: 'We cut your reel in ~15–30 minutes',
  onboardingEmailLabel: 'Email',
  onboardingEmailOptional: '(optional — for account updates)',
  onboardingEmailPlaceholder: 'you@example.com',
  onboardingSaving: 'Saving…',
  onboardingSaveFailed: 'Could not save your profile. Please try again.',
  vodsEmptyHeadline: 'No VODs found on your Twitch channel yet',
  vodsEmptyBody: 'Publish a VOD on Twitch, then come back here to cut a reel.',
  vodsUnsupportedHeadline: 'No Madden or CFB VODs to show',
  vodsUnsupportedBody:
    'Scoutlytics Studio supports Madden and College Football. Other games stay hidden.',
  vodUnsupportedGame:
    "This VOD isn't a supported game — Scoutlytics Studio supports Madden & CFB",
  tryAgain: 'Try again',
  loadAccountFailed: 'Could not load your account. Please try again.',
  loadStudioFailed: 'Could not load your studio. Please try again.',
  sceneNotFound: 'Scene not found',
  sceneNotFoundBody: 'That studio route does not exist.',
  backToStudio: 'Back to studio',
  checkoutCanceledHeadline: 'Checkout canceled',
  checkoutCanceledBody:
    "No charge was made. You can pick a plan again whenever you're ready.",
  backToPlans: 'Back to plans',
  backToVods: 'Back to VODs',
  successAlmostThere: 'Almost there',
  successRefreshing: 'Confirming your upgrade…',
  refreshCta: 'Refresh',
  accountPlanHeading: 'Plan',
  accountAdminUnlimited: 'Unlimited admin access',
  portalOpening: 'Opening…',
  monthlyLimitReached: 'Monthly limit reached',
  reconnectHeadline: 'Reconnect Twitch',
  reconnectBody:
    "Your Twitch connection needs a refresh before we can list your VODs. This is normal after a while — reconnect and you'll be back in the suite.",
  reconnectCta: 'Reconnect Twitch',
  accountHeading: 'Account',
  accountSubhead: 'Your identity and plan.',
  upgradeCta: 'Upgrade to Studio Pro',
  betaLimitReached: 'Beta limit reached',
  betaLimitReachedBody:
    "You've used your free reel — upgrade to Pro for 10 reels a month.",
  watermarkUpsell:
    'Free reels carry a watermark. Go Pro for clean reels — 10 a month, no watermark.',
  cancelAnytime: 'Cancel anytime.',
  resultHeadline: 'Your reel is cut.',
  previewHeadline: 'Preview ready',
  previewBadge: 'Preview',
  previewBlurb:
    'Watermarked preview — Approve & Download to unlock the clean reel (uses 1 of your reel allowance).',
  acceptCta: 'Approve & Download',
  acceptCtaBusy: 'Finalizing clean reel…',
  acceptCtaWithCost: 'Approve & Download · uses 1 reel',
  acceptCostTemplate: 'Uses 1 of {remaining} reels left this cycle.',
  acceptCostFreeTemplate: 'Uses 1 of {remaining} free {noun} remaining.',
  acceptCostUnlimited: 'Approve unlocks a clean reel (admin — no dock).',
  acceptCleanHint: 'Removes the watermark and unlocks download.',
  generationCeilingHint:
    'Beta limit reached for re-rolls this period. Trim is still free.',
  alreadyExists: 'You already have a reel for this VOD — here it is.',
  recapLockLabel: 'Game Recap',
  recapLockBlurb: 'The full story of your stream, auto-cut — both sides of the ball.',
  highlightComingSoon: 'Team-specific highlights — coming soon',
  highlightNotifyCta: 'Notify me',
  highlightNotifyConfirm: "You're on the list",
  recutConfirmTitle: 'Re-cut this reel?',
  recutCostTemplate: 'Uses 1 of your {remaining} reels left this cycle.',
  recutCostFreeTemplate: 'Uses 1 of your {remaining} free {noun} remaining.',
  recutCostUnlimited: 'Re-cut runs a full new detect + render (admin — no dock).',
  recutGenCostTemplate:
    'Starts a new watermarked preview — {remaining} of {ceiling} generations left this period.',
  recutGenCostFreeTemplate:
    'Starts a new watermarked preview — {remaining} of {ceiling} generations left.',
  recutButton: 'Re-cut this reel',
  recutConfirm: 'Re-cut (use 1 reel)',
  recutConfirmSignoff: 'Re-cut (new preview)',
  recutConfirmReuse: 'Re-cut (free)',
  recutReuseCost:
    'Reuses the existing analysis — free, no new scan.',
  recutRescan: 'Re-scan from scratch',
  recutRescanHint: 'Runs a full new detect. Uses a generation.',
  recutCancel: 'Keep current reel',
  recutBusy: 'Starting re-cut…',
  durationLabel: 'Reel length',
  durationShort: '0:30',
  durationShortHint: 'Quick hits',
  durationMid: '1:30',
  durationMidHint: 'Standard',
  durationLong: 'Up to 2:30',
  durationLongHint: 'Best available',
  durationBlurb:
    'Same analysis — changing length re-cuts clips, it does not re-scan.',
  durationSwitchHint: 'Instant — reuses the existing analysis (no new scan).',
  durationSwitchBusy: 'Re-cutting to a new length…',
  durationSwitchFailed: 'Could not change reel length. Try again.',
  durationSwitchSourceExpired:
    "This reel's original footage has expired, so its length can't be changed here. Re-scan to pick a new length.",
  durationSwitchRescan:
    'Re-scan — runs a new analysis, counts as a new reel',
  durationSwitchRescanHint:
    'Starts a fresh detect. Uses a reel credit or free allowance — it is not a free length change.',
  durationSwitchNotReady:
    "This reel isn't ready to re-cut yet — try again in a moment.",
  durationSwitchDisabled: "Length change isn't available right now.",
  durationSwitchNotFound: "This reel couldn't be found.",
  durationSwitchNetwork:
    "Couldn't reach Stream Center. Check your connection and try again.",
  durationSwitchAuth: 'Please sign in with Twitch to continue.',
  refineOpen: 'Review & refine',
  refineHeadline: 'Review & refine',
  refineBlurb:
    'Drop a bad clip or nudge the start/end. Rebuild preview is free — it does not use a reel.',
  refineNoDockNote:
    'Preview and rebuild are free. Only Approve & Download uses 1 reel.',
  refineSubmit: 'Rebuild preview — free',
  refineBusy: 'Rendering watermarked preview…',
  refineCancel: 'Back',
  refineCancelDiscard: 'Discard changes',
  refineDrop: 'Drop',
  refineKeep: 'Keep',
  refineTrimStartEarlier: 'Start −0.5s',
  refineTrimStartLater: 'Start +0.5s',
  refineTrimEndEarlier: 'End −0.5s',
  refineTrimEndLater: 'End +0.5s',
  refineEmptyError: 'Keep at least one clip.',
  refineFailed: 'Could not refine this reel. Try again.',
  customizeOpen: 'Customize',
  addonsTitle: 'Add-ons',
  addonsBlurb: 'Stack music and your own voice over the reel. Video stays identical — audio only.',
  addonsOpen: 'Add-ons',
  addonsLocked: 'Music and voiceover are included with Pro.',
  addonsWatermark: 'Add watermark',
  addonsWatermarkOn: 'Watermark added',
  addonsWatermarkOnPill: 'On',
  addonsWatermarkBlurb:
    'Add a watermark — badges your reel so viewers know where it’s from (great for sharing). Free to toggle on or off — no credit, no new reel.',
  addonsWatermarkApplying: 'Adding badge…',
  addonsWatermarkReapplying: 'Re-applying watermark…',
  addonsWatermarkFreeNote: 'Free reels include a watermark.',
  addonsWatermarkFailed: 'Could not update the watermark. Your original reel is unchanged.',
  addonsWatermarkStillWorking:
    "Still adding the badge — it'll appear on your reel shortly. Refresh to check.",
  addonsUpgradeCta: 'Upgrade',
  addonsApply: 'Apply',
  addonsApplying: 'Mixing…',
  addonsSaveClose: 'Save & close',
  addonsRemove: 'Remove add-ons',
  addonsRemoving: 'Removing…',
  addonsApplied: 'Mix is on — share and download use this version.',
  addonsFailed: 'Could not mix add-ons. Your original reel is unchanged.',
  addonsMixStillWorking:
    'Still mixing — this can take a few minutes on longer reels. Refresh to check.',
  addonsCooldown: "You're mixing this reel a lot — wait a minute and try again.",
  addonsTrack: 'Track',
  addonsMix: 'Mix',
  addonsMusicVolume: 'Music',
  addonsReelAudioVolume: 'Reel audio',
  addonsVoiceVolume: 'Voice',
  addonsVoiceover: 'Voiceover',
  addonsVoiceRecord: 'Record',
  addonsVoiceRerecord: 'Re-record',
  addonsVoiceStop: 'Stop',
  addonsVoiceRecording: 'Recording',
  addonsVoiceUnsupported:
    "Voice recording isn't supported in this browser — record in Chrome, or upload a voice file instead",
  addonsVoiceMonitor: 'Monitor (use headphones)',
  addonsVoicePreview: 'Play / preview',
  addonsVoiceClearTake: 'Clear take',
  addonsVoiceTakeLength: 'Take {length}',
  addonsVoiceUpload: 'Upload',
  addonsVoiceRightsAck: 'I have the rights to this audio',
  addonsVoiceUploadOnly:
    'Uploaded voiceover: download-only — not on your public share link.',
  addonsMusicUpload: 'Upload your own',
  addonsMusicUploadOnly:
    'Uploaded music: download-only — not on your public share link.',
  addonsUploadOnly:
    'Uploaded audio: download-only — not on your public share link.',
  addonsUploadedTrackRemove: 'Remove',
  addonsSummaryMusic: 'Music applied',
  addonsSummaryVoice: 'Voiceover applied',
  addonsSummaryBoth: 'Music + voiceover applied',
  addonsSummaryEdit: 'Edit',
  addonsPreview: 'Preview',
  addonsStopPreview: 'Stop',
  customizeHeadline: 'Customize your reel',
  customizeBlurb:
    'Pick the plays, arrange them on a timeline, then approve. Rebuild preview is free — Approve & Download uses 1 reel.',
  customizeStepper: '1 · Pick your plays → 2 · Arrange & trim → 3 · Approve & download.',
  customizeStep1Title: 'Pick your plays',
  customizeStep1Hint:
    "Pick the plays you want. We've pre-selected the best ones — add or remove any.",
  customizeStep2Title: 'Arrange & trim',
  customizeStep2Hint:
    "Drag to reorder or trim — the Adjusted reel plays your arrangement and music instantly. Rebuild for the exact watermarked file.",
  customizeStep3Title: 'Approve & download',
  customizeNextArrange: 'Next: Arrange & trim →',
  customizeBackToPick: '← Back to picks',
  customizeSubmit: 'Render watermarked preview (with music) — free',
  customizeReelStale:
    'Edits pending — instant preview (with music) is live; Rebuild for the exact watermarked file.',
  customizeClipPlayHint: 'Clips play in the preview window above.',
  customizePlayerClipTab: 'Selected clip',
  customizePlayerReelTab: 'Adjusted reel',
  customizeInstantTitle: 'Preview arrangement (instant):',
  customizeInstantSubtitle:
    'Approximate cut from source with your selected music mix. Rebuild renders the exact watermarked file.',
  customizeInstantReplay: 'Replay arrangement',
  customizeWatermarkedTab: 'Watermarked (exact)',
  customizeInstantTab: 'Arrangement (instant)',
  customizeHowTitle: 'How it works',
  customizeHowStep1Title: 'Pick your plays',
  customizeHowStep1Body:
    "We've auto-selected the best plays from your game. Tap any play to add or remove it — selected plays turn green and get a number showing the order they'll appear. Tap ▶ on a play to watch it before deciding. Include up to 20 plays / 3 minutes.",
  customizeHowStep2Title: 'Add music (optional)',
  customizeHowStep2Body:
    'Tap ▶ next to any of the 14 tracks to hear it instantly. Pick one to lay under your reel, or keep “No music.” Use the two sliders to balance game sound against the music.',
  customizeHowStep3Title: 'Reorder',
  customizeHowStep3Body:
    'On the timeline, drag any clip left or right to change where it plays. The numbers update as you move it.',
  customizeHowStep4Title: 'Trim',
  customizeHowStep4Body:
    "Drag a clip's left or right edge to tighten it. You can shorten a clip but not extend it past the play we detected — that's what keeps every clip clean, with no dead time or cut-off action.",
  customizeHowStep5Title: 'Preview instantly',
  customizeHowStep5Body:
    'The “Arrangement (instant)” view plays your current order, trims, and music right away — no waiting. It\'s an approximate cut straight from the source.',
  customizeHowStep6Title: 'Render the exact file (Rebuild)',
  customizeHowStep6Body:
    "When you want the precise watermarked version with final audio mixing, tap Rebuild. It's free and doesn't use a reel.",
  customizeHowStep7Title: 'Approve & download',
  customizeHowStep7Body:
    'Happy with it? Approve removes the watermark, renders the clean final reel, and unlocks your download. This is the only step that uses one of your reels.',
  customizePreviewTooltip: 'Play this clip in the preview above',
  customizePickSelect: 'Select',
  customizePickSelected: 'Selected',
  customizePickPreview: '▶',
  customizeSelectedTray: '{count} selected',
  customizeEdgeAtBound: 'At safe edge',
  customizeInReel: 'In your reel',
  customizePool: 'More plays we found',
  customizePoolOrderNote: 'Ordered by spectacle score — preview before you add.',
  customizePoolEmpty: 'Every detected play is already in your reel.',
  customizeAdd: 'Add',
  customizeStatsTemplate:
    '{count} of {max} clips · ~{duration}s / {maxDuration}s max',
  customizeClipCap: 'Too many clips — keep at most {max}.',
  customizeDurationCap: 'Reel too long — keep total clip time under {max}s.',
  customizeTotalTemplate: 'Total {total}',
  musicPickerTitle: 'Music',
  musicTrackLabel: 'Track',
  musicNone: 'No music',
  musicVocalsTag: 'Vocals',
  musicVocalHint:
    'Vocals auto-switch to music-only and duck game audio so they don’t fight commentary.',
  musicModeLabel: 'Mix mode',
  musicModeBed: 'Bed (under game)',
  musicModeOnly: 'Music only',
  musicGameVolume: 'Clip / game audio',
  musicMusicVolume: 'Music',
  musicLaneEmpty: 'Add music',
  musicLaneLabel: 'Music',
  musicRebuildNote:
    'Instant preview loops the bed client-side (approx) — Rebuild renders exact loop + fades.',
  musicLoopLabel: 'Loop music to fill reel',
  musicFadeLabel: 'Fade in / out',
  musicFadeInLabel: 'Fade in (s)',
  musicFadeOutLabel: 'Fade out (s)',
  progressReassurance:
    'Most reels are ready in ~15–30 minutes depending on broadcast length. You can safely close or leave this page — your reel will be saved in My Reels.',
  copyLinkNote: 'Anyone with this link can watch. The link does not expire.',
  copiedConfirm: 'Copied!',
  downloadLabel: 'Download',
  downloadWorking: 'Saving…',
  downloadHoldHint: 'Tap and hold the video, then Save Video',
  publicWatchUnavailable: "This reel isn't available",
  publicWatchByline: 'Cut with Scoutlytics Studio',
  publicWatchCta: 'Make your own →',
  publicWatchCtaHref: 'https://scoutlytics.gg',
  publicWatchHowHeading: 'Make one like this in 3 steps',
  publicWatchHowSubhead: 'No editing. No timeline. Just your stream.',
  publicWatchStep1Title: 'Paste your Twitch VOD',
  publicWatchStep1Body:
    "Drop the link to any stream. That's the only thing we need.",
  publicWatchStep2Title: 'AI finds every big play',
  publicWatchStep2Body:
    'Our engine reads the scoreboard and pulls the moments that matter.',
  publicWatchStep3Title: 'Share your reel anywhere',
  publicWatchStep3Body:
    'Get a clean link like this one — Discord, X, anywhere.',
  publicWatchProof1Stat: 'Madden + CFB',
  publicWatchProof1Line: 'auto-cut from your stream',
  publicWatchProof2Stat: '$19.99/mo',
  publicWatchProof2Line: 'Pro — no watermark',
  publicWatchProof3Stat: '1 free reel',
  publicWatchProof3Line: 'no card required',
  publicWatchHowCta: 'Make your first reel — free',
  publicWatchHowCtaHref: 'https://scoutlytics.gg/studio/upgrade',
  publicWatchHowCtaHint: 'Free to start · no card required',
  linkExpired: 'This link expired — generate again from your VODs.',
  upgradeComingSoon: 'Upgrade coming soon',
  checkoutPending: 'Taking you to checkout…',
  checkoutUnavailable: "Checkout isn't available right now — try again shortly.",
  successActivating: 'Activating your account…',
  successProHeadline: "You're Pro. 10 reels a month, no watermark.",
  successPackHeadline: 'Your 5 clean reels are ready.',
  successPackBody: 'No subscription. Generate anytime — they don’t expire.',
  creditBalanceTemplate: '{n} clean reels left',
  creditBalanceFull: '5 clean reels',
  successFallback:
    'This is taking longer than usual — your access will appear shortly. Refresh in a moment.',
  planNoWatermark: 'No watermark',
  planAddons: 'Music + voiceover add-ons',
  planReelsPerMonth: '10 reels/mo',
  /** Cap-reached template: interpolate {period_cap} and {period_resets_at}. */
  capReachedTemplate:
    "You've hit your {period_cap} reels this month. Resets {period_resets_at}.",
  capReachedNeedMore: 'Need more? Contact us.',
  capReachedRenewal: 'Your reels refresh automatically.',
  allotmentTemplate: '{remaining} of {cap} reels left this month · resets {period_resets_at}',
  freeAllotmentTemplate: '{remaining} of {cap} free {noun} left',
  freeCapReached:
    "You've used your free reel — upgrade to Pro for 10 reels a month.",
  vodTooShortHint: 'Too short to reel',
  vodsAllTooShortHeadline: 'None of your recent VODs are long enough yet.',
  vodsAllTooShortBody:
    'Stream a full game (10+ min) and it\'ll show up here.',
  freeReelRemainingTemplate: '{remaining} free reel remaining',
  freeReelsRemainingTemplate: '{remaining} free reels remaining',
  freeReelUsed: "You've used your free reel — upgrade to Pro for 10 reels a month.",
  unlimitedBadge: 'Unlimited',
  vodTooShortGenerate:
    'That VOD is too short to cut a reel. Pick one that\'s at least 10 minutes.',
  generateFailedFallback: 'Generation failed — please retry.',
  generateFailedRetry: 'Generation failed — please retry.',
  /** Never use this for an in-flight or delivered job. */
  pollTimedOut: 'Generation failed — please retry. This attempt did not use a reel.',
  pollStillProcessing:
    'Still processing — check your library shortly. This attempt has not failed.',
  noCandidatesFailed:
    "We couldn't find any highlight plays in this VOD. Try a different VOD, or a longer game with more scoring plays.",
  noCandidatesCta: 'Try another VOD',
  failedCutNoChargeNote:
    "This attempt didn't use a reel or generation — you can safely retry.",
  notAllowlisted:
    'Friends beta is invite-only. This account is not on the allowlist.',
  generationPaused:
    'Generation is temporarily paused for the friends beta. Please try again later.',
  dailyCapacity: "We're at today's capacity — try tomorrow.",
  dailyCapacityHeadline: "We're at today's capacity",
  dailyCapacityBody:
    "We've hit today's generation limit. Your allotment is unchanged — try again tomorrow.",
  detectQueued: 'High demand — your reel is queued and will start shortly.',
  feedbackCta: 'Send feedback',
  feedbackHeadline: 'Friends beta feedback',
  feedbackBlurb: 'Tell us what worked, what broke, or what you want next.',
  feedbackPlaceholder: 'Your notes…',
  feedbackSubmit: 'Send',
  feedbackSending: 'Sending…',
  feedbackThanks: 'Thanks — we got it.',
  feedbackError: "Couldn't send feedback — try again or use email.",
  feedbackMailtoLabel: 'Email us instead',
  invalidEmail: 'Enter a valid email',
  portalUnavailable: "Couldn't open billing — try again.",
  onboardingWelcome: 'Welcome to Scoutlytics Studio',
  onboardingContinue: 'Continue',
  accountManageSubscription: 'Manage subscription',
  accountSignOut: 'Sign out',
  reportProblem: 'Report a problem',
  reportProblemReel: 'Something went wrong with this reel',
  reportProblemSuccess: "Thanks — we got it. We'll follow up and make it right.",
  reportProblemSubmit: 'Submit',
  myReportsTitle: 'Your reports',
  adminSupportTitle: 'Support inbox',
  adminSupportGrant: 'Grant clean credits',
  adminSupportRestore: 'Restore the exact reel',
  adminSupportConfirmGrant: 'Grant credits to this user? This cannot be undone from this screen.',
  adminSupportConfirmRestore: 'Restore a free reel slot for this user?',
  adminSupportOpen: 'Open',
  adminSupportArchived: 'Archived',
  adminSupportDeleted: 'Deleted',
  adminSupportReply: 'Reply',
  adminSupportResolve: 'Resolve',
  adminSupportReopen: 'Reopen',
  adminSupportDelete: 'Delete',
  adminSupportRestoreTicket: 'Restore',
  adminSupportConfirmDelete:
    'Move this report to Deleted? You can restore it. Credit grants and free-reel restores are not removed.',
  adminSupportDeletedHint: 'Moved to Deleted — you can restore it.',
  supportReplyPlaceholder: 'Write a reply…',
  supportUnread: 'New reply',
  accountUpgradeLink: 'Upgrade to Studio Pro',
  accountPackCta: 'Get 5 clean reels — $14.99 (one-time)',
  freePlanLabel: 'Friends beta — capped free reels',
  paidPlanLabel: 'Studio Pro',
  reelsEmptyHeadline: 'No reels yet.',
  reelsEmptyCta: 'Pick a VOD',
  reelsNavLabel: 'My Reels',
  reelsLeaveHint: "You can leave — we'll keep cutting. Find it in My Reels.",
  reelsLeaveCta: 'Go to My Reels',
  reelsReadyToastPrefix: 'Your reel is cut ✂️',
  reelsUntitled: 'Highlight reel',
  publicReelFallback: 'Highlight reel',
  reelsNameThisReel: 'Name this reel',
  reelsNameThisReelSave: 'Save name',
  reelsRename: 'Rename',
  reelsRenameSave: 'Save',
  reelsRenameCancel: 'Cancel',
  reelsRenameError: "Couldn't rename that reel — try again.",
  reelNameLabel: 'Name your reel',
  reelNamePlaceholder: 'e.g. Week 4 comeback',
  reelNameRequired: 'Name your reel to cut it.',
  reelPosterAlt: 'Reel thumbnail',
  reelsDeleteConfirm: 'Delete this reel?',
  reelsDeleteError: "Couldn't delete that reel — try again.",
  reelsCuttingBadge: 'Cutting…',
  reelsFailedBadge: 'Failed',
  railHomeLabel: 'Home',
  railVodsLabel: 'VODs',
  railReelsLabel: 'Reels',
  railLscLabel: 'League Stream Center',
  railPlansLabel: 'Plans',
  railAccountLabel: 'Account',
  mobileAppBannerLabel: 'Mobile App',
  mobileAppBannerCta: 'Get the Mobile App',
  mobileAppBannerBody: 'Install Scoutlytics Studio on your phone.',
  mobileAppMenuLabel: 'Get the Mobile App',
  mobileAppInstalledLabel: 'Installed',
  mobileAppModalTitle: 'Get the Scoutlytics Studio Mobile App',
  mobileAppInstall: 'Install',
  mobileAppInstalledToast:
    'Installed — look for Studio on your home screen.',
  mobileAppIosShareStep: 'Tap the Share icon',
  mobileAppIosShareHint:
    'The Share icon is at the bottom of Safari — a square with an arrow pointing up.',
  mobileAppIosAddStep: "Choose 'Add to Home Screen'",
  mobileAppIosConfirmStep: 'Tap Add',
  mobileAppIosOpenSafari:
    'Open scoutlytics.gg in Safari to add the Mobile App to your home screen',
  mobileAppGeneric:
    "Open your browser menu and choose 'Install app' / 'Add to Home screen'.",
  mobileAppDismissBanner: 'Dismiss Mobile App banner',
  mobileAppCloseModal: 'Close',
  authResolveFailed:
    'Could not verify your sign-in. Try again or connect Twitch.',
  footerBrand: 'Scoutlytics Studio',
  landingHeadline: 'It watches the whole game.',
  landingSubhead:
    'Scoutlytics Studio watches your entire Twitch VOD — both sides of the ball — and cuts the story of the game. Not the loudest thirty seconds. The game.',
  landingLimitEta: '~15–30 minutes, not seconds.',
  landingLimitExpiry: "Twitch VODs expire — cut the reel while it's up.",
  landingLimitRetention:
    'Twitch deletes past broadcasts after 7–60 days depending on your plan.',
  landingLimitTitle: 'Madden football today.',
  landingReelCaption: 'A real reel, start to finish. Nothing rearranged.',
  landingDiffHeading: "Why this isn't a clipper.",
  landingDiffSub:
    'Clip tools are built to find moments people reacted to. That is a different job from telling the story of a game.',
  landingDiffReactionTitle: 'They watch the reaction. We watch the game.',
  landingDiffReactionBody:
    'Clip tools score chat spikes, audio peaks, sentiment and trending-topic signals. That finds the moment the room got loud. It does not find a third-and-long stop that nobody in chat noticed.',
  landingDiffOrderTitle: 'They rearrange. We keep the order.',
  landingDiffOrderBody:
    'A general clipper assembles the "best" moments into a short. Studio returns your plays in the order they happened, so the reel plays like the game did — and can actually be posted as a recap.',
  landingDiffFootballTitle: "They don't know football. That's all we do.",
  landingDiffFootballBody:
    "A general tool has no concept of downs, possession, or two teams trading drives. Your defense never makes its cut, because it doesn't know your defense was on the field.",
  landingHowHeading: 'How it works',
  landingHowConnectTitle: 'Connect Twitch',
  landingHowConnectBody: "We read your own channel's VODs, nothing else.",
  landingHowPickTitle: 'Pick a VOD',
  landingHowPickBody: 'Any stream 10 minutes or longer.',
  landingHowRunTitle: 'We run the tape',
  landingHowRunBody: 'The full broadcast, analyzed end to end.',
  landingHowReelTitle: 'Get your reel',
  landingHowReelBody: 'Play it, download it, post it.',
  landingLimitsHeading: 'Straight about the limits',
  landingPricingHeading: 'Pricing',
  landingPricingPaidTemplate:
    'From {price} — {n} reels/mo, no watermark, cancel anytime.',
  landingSeePlansHint: 'Compare Free and Pro — watermark, reel count, and what you get back.',
  landingLscHeading: 'Run a league?',
  landingLscBody:
    "League Stream Center turns your league's streams into a verified replay archive your members actually watch.",
  landingLscCta: 'League Stream Center',
  studioPageHeading: 'Studio',
  homeNextVodKicker: "Your latest stream hasn't been cut yet",
  homeNextVodCta: 'Cut this reel',
  homeCaughtUp: "You're caught up. Stream a game and it'll show up here.",
  homeNoVodsHeadline: 'Connect Twitch to pull in your VODs.',
  homeNoVodsCta: 'Connect Twitch',
  homeRecentHeading: 'Recent reels',
  homeViewAll: 'View all →',
  homeProviderTwitch: 'Twitch',
  expectationHeadline: "What you'll get back:",
  expectationTouchdowns: 'Touchdowns',
  expectationExplosives: 'Explosive gains',
  expectationTurnovers: 'Turnovers',
  expectationStops: 'Defensive stops',
  expectationChrono:
    'Both sides of the ball, in the order the game happened.',
  expectationLeftOut:
    'Left out: extra points · touchbacks · kneel-downs · no-gainers',
  vodsRetentionLine:
    'Twitch deletes past broadcasts after 7–60 days depending on your plan. Cut your reels while the VODs are up.',
  statusReady: 'Ready',
  vodsRefreshCta: 'Refresh',
  vodsPageHeading: 'Pick a VOD',
  vodsPageSubhead: "Choose one of your Twitch VODs. We'll cut a game recap from it.",
  reelsPageHeading: 'My Reels',
  reelsFilterAll: 'All',
  reelsFilterDelivered: 'Delivered',
  reelsFilterInProgress: 'In progress',
  reelsFilterFailed: 'Failed',
  reelsEmptyBody: "Pick a VOD and we'll cut the first one. It's free.",
  reelsEmptyInProgress: 'No reels in progress.',
  reelsEmptyDelivered: 'No delivered reels.',
  reelsEmptyFailed: 'No failed reels.',
  expiryDaysLeftTemplate: '{n} days left',
  expiryDayLeft: '1 day left',
  expiryHoursLeftTemplate: '{n} hours left',
  expiryHourLeft: '1 hour left',
  reelContextTemplate:
    'Plays in the order they happened, both sides of the ball — cut from your {stream date} stream.',
  reelContextUndated:
    'Plays in the order they happened, both sides of the ball.',
  clipCountTemplate: '{n} clips',
  recapEditorV2Preview: 'Preview ▶',
  recapEditorV2Finalize: 'Finalize Recap · 1 reel',
  recapEditorV2Draft: 'Draft · not finalized',
  recapEditorV2Finalizing: 'Finalizing…',
  recapEditorV2Finalized: 'Finalized',
  recapEditorV2CarryFootnote:
    'Add-ons apply to preview; download carries them once finalize-carry ships.',
  recapEditorV2LivePreview: '● Live preview',
  recapEditorV2HearSave:
    'What you hear is what gets saved — preview matches the final recap.',
  recapEditorV2IncludesTitle: 'This recap includes',
  recapEditorV2ToolClips: 'Clips',
  recapEditorV2ToolMix: 'Audio Mix',
  recapEditorV2ToolMusic: 'Music',
  recapEditorV2ToolVoice: 'Voice',
  recapEditorV2ToolBranding: 'Branding',
  recapEditorV2AutoArrange: 'Auto-arrange',
  recapEditorV2Snap: 'Snap',
  recapEditorV2NoVoice: 'No voiceover',
  recapEditorV2VoiceOn: 'Voiceover',
  recapEditorV2NoWatermark: 'No watermark',
  recapEditorV2WatermarkOn: 'Watermark',
  recapEditorV2NoMusic: 'No music',
  recapEditorV2GameAudio: 'Game audio',
  recapEditorV2MusicBed: 'Music bed',
  recapEditorV2Voiceover: 'Voiceover',
  recapEditorV2Muted: 'Muted',
  recapEditorV2MuteGame: 'Mute game audio',
  recapEditorV2ShareNote: 'Public watch page uses clean audio only.',
} as const;

/** Per-tier features from the real product lock — do not invent extras. */
export const STUDIO_FREE_FEATURES = [STUDIO_COPY.pricingFreeValue] as const;

export const STUDIO_PRO_FEATURES = [
  STUDIO_COPY.planReelsPerMonth,
  STUDIO_COPY.planNoWatermark,
  STUDIO_COPY.planAddons,
  STUDIO_COPY.recapLockBlurb,
  STUDIO_COPY.cancelAnytime,
] as const;

/**
 * Exclusion claims ("Left out: extra points · touchbacks · …").
 * Held until a real v1-detect production job shows
 * `draft_diagnostics.dropped_by_playtype` count > 0 with PAT/kickoff types.
 * `strict_boundaries: true` only proves the gate is enabled, not that it fires.
 * Positive selection (TDs / explosives / turnovers / stops) always ships.
 */
export const STUDIO_SELECTION_CLAIMS_ENABLED = false;

export function isNoCandidatesError(errorCode?: string | null): boolean {
  return (errorCode || '').trim() === 'no_candidates';
}

export function isDeadJobError(errorCode?: string | null): boolean {
  const code = (errorCode || '').trim().toUpperCase();
  return (
    code === 'QUEUE_DEAD' ||
    code === 'STUCK_TIMEOUT' ||
    code === 'DEAD_JOB' ||
    (errorCode || '').trim() === 'stuck_timeout'
  );
}

export function formatGenerateFailedCopy(errorCode?: string | null): string {
  if (isNoCandidatesError(errorCode)) {
    return STUDIO_COPY.noCandidatesFailed;
  }
  if (isDeadJobError(errorCode)) {
    return STUDIO_COPY.generateFailedRetry;
  }
  const code = (errorCode || '').trim();
  if (!code) return STUDIO_COPY.generateFailedFallback;
  return `${STUDIO_COPY.generateFailedRetry} (${code})`;
}

export function formatCapReachedCopy(
  periodCap: number,
  periodResetsAtLabel: string
): string {
  return STUDIO_COPY.capReachedTemplate
    .replace('{period_cap}', String(periodCap))
    .replace('{period_resets_at}', periodResetsAtLabel);
}

export function formatAllotmentCopy(
  remaining: number,
  cap: number,
  periodResetsAtLabel?: string
): string {
  const reset = periodResetsAtLabel || 'next month';
  return STUDIO_COPY.allotmentTemplate
    .replace('{remaining}', String(remaining))
    .replace('{cap}', String(cap))
    .replace('{period_resets_at}', reset);
}

export function formatFreeAllotmentCopy(remaining: number, cap: number): string {
  const noun = cap === 1 ? 'reel' : 'reels';
  return STUDIO_COPY.freeAllotmentTemplate
    .replace('{remaining}', String(remaining))
    .replace('{cap}', String(cap))
    .replace('{noun}', noun);
}

export function formatAcceptCostFreeCopy(remaining: number): string {
  const noun = remaining === 1 ? 'reel' : 'reels';
  return STUDIO_COPY.acceptCostFreeTemplate
    .replace('{remaining}', String(remaining))
    .replace('{noun}', noun);
}

export function formatRecutCostFreeCopy(remaining: number): string {
  const noun = remaining === 1 ? 'reel' : 'reels';
  return STUDIO_COPY.recutCostFreeTemplate
    .replace('{remaining}', String(remaining))
    .replace('{noun}', noun);
}

export function formatCreditBalanceCopy(credits: number): string {
  if (credits === STUDIO_PRICING.pack5.reels) {
    return STUDIO_COPY.creditBalanceFull;
  }
  return STUDIO_COPY.creditBalanceTemplate.replace('{n}', String(Math.max(0, credits)));
}

export function defaultCheckoutPlan(): Extract<StudioCheckoutPlan, 'monthly' | 'annual'> {
  return STUDIO_PRICING.annual.defaultSelected ? 'annual' : 'monthly';
}
